import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OverviewCardComponent } from '../../../../../shared/charts/components/elements/overview-card/overview-card.component';
import { TablePaginationComponent } from '../../../../../shared/components/table-pagination/table-pagination.component';
import { TableSkeletonRowComponent } from '../../../../../shared/components/table-skeleton-row/table-skeleton-row.component';
import { WalletApiService } from '../../../services/wallet-api.service';
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';
import { ToastNotificationType, ToastPosition } from '../../../../../notifications/models/toast-notification-type';
import { Wallet } from '../../../models/wallet';
import { WalletTransaction, WalletTransactionKind } from '../../../models/wallet-transaction';
import { WalletPayout } from '../../../models/wallet-payout';
import { LimitOffsetPagination } from '../../../../../shared/models/limit-offset-pagination';
import { GenericPaginationResponseBody } from '../../../../../shared/models/Generic-pagination-response-body';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { FiltersDropdownComponent, FilterDef, FilterValues } from '../../../../../shared/filters-dropdown';
import { ExportExcelButtonComponent } from '../../../../../shared/components/export-excel-button/export-excel-button.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../../shared/services/translations/language.service';

@Component({
  selector: 'app-wallet',
  imports: [CommonModule, FormsModule, OverviewCardComponent, TablePaginationComponent, TableSkeletonRowComponent, FiltersDropdownComponent, ExportExcelButtonComponent, TranslateModule],
  templateUrl: './wallet.component.html',
  styleUrl: './wallet.component.css'
})
export class WalletComponent implements OnInit {

  apiService = inject(WalletApiService);
  toastNotificationService = inject(ToastNotificationService);
  translate = inject(TranslateService);
  languageService = inject(LanguageService);

  wallet: Wallet | undefined;
  transactionsResponse: GenericPaginationResponseBody<WalletTransaction> | undefined;
  payoutsResponse: GenericPaginationResponseBody<WalletPayout> | undefined;

  transactionsPagination: LimitOffsetPagination = { limit: 5, offset: 0 };
  payoutsPagination: LimitOffsetPagination = { limit: 5, offset: 0 };

  transactionsSearch = '';
  payoutsSearch = '';
  transactionsSearchSubject = new Subject<string>();
  payoutsSearchSubject = new Subject<string>();

  transactionsFilterValues: FilterValues = {};
  payoutsFilterValues: FilterValues = {};

  transactionsFilters: FilterDef[] = [];
  payoutsFilters: FilterDef[] = [];


  ngOnInit(): void {
    this.transactionsFilters = this.buildTransactionsFilters();
    this.payoutsFilters = this.buildPayoutsFilters();

    this.getWallet();
    this.getTransactions();
    this.getPayouts();

    this.transactionsSearchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
    ).subscribe(searchText => {
      this.transactionsSearch = searchText;
      this.transactionsPagination = { limit: this.transactionsPagination.limit, offset: 0 };
      this.getTransactions();
    });

    this.payoutsSearchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
    ).subscribe(searchText => {
      this.payoutsSearch = searchText;
      this.payoutsPagination = { limit: this.payoutsPagination.limit, offset: 0 };
      this.getPayouts();
    });

    this.translate.onLangChange.subscribe(() => {
      this.transactionsFilters = this.buildTransactionsFilters();
      this.payoutsFilters = this.buildPayoutsFilters();
      this.getWallet();
      this.getTransactions();
      this.getPayouts();
    });
  }

  private buildTransactionsFilters(): FilterDef[] {
    const currency = this.translate.instant('common.currency');
    return [
      {
        kind: 'numberRange',
        key: 'amount',
        label: `${this.translate.instant('wallet.filters.amount')} (${currency})`,
        minPlaceholder: this.translate.instant('wallet.filters.from'),
        maxPlaceholder: this.translate.instant('wallet.filters.to'),
      },
      {
        kind: 'select',
        key: 'kind',
        label: this.translate.instant('wallet.filters.kind'),
        placeholder: this.translate.instant('wallet.filters.all_kinds'),
        options: [
          { label: this.translate.instant('wallet.transactions.kinds.pending_credit'), value: 'pending_credit' },
          { label: this.translate.instant('wallet.transactions.kinds.pending_release'), value: 'pending_release' },
          { label: this.translate.instant('wallet.transactions.kinds.pending_settle'), value: 'pending_settle' },
          { label: this.translate.instant('wallet.transactions.kinds.available_reversal'), value: 'available_reversal' },
          { label: this.translate.instant('wallet.transactions.kinds.payout'), value: 'payout' },
        ],
      },
      {
        kind: 'dateRange',
        key: 'createdAt',
        label: this.translate.instant('wallet.filters.date'),
        placeholder: this.translate.instant('wallet.filters.date_placeholder'),
      },
    ];
  }

  private buildPayoutsFilters(): FilterDef[] {
    const currency = this.translate.instant('common.currency');
    return [
      {
        kind: 'numberRange',
        key: 'amount',
        label: `${this.translate.instant('wallet.filters.amount')} (${currency})`,
        minPlaceholder: this.translate.instant('wallet.filters.from'),
        maxPlaceholder: this.translate.instant('wallet.filters.to'),
      },
      {
        kind: 'select',
        key: 'status',
        label: this.translate.instant('wallet.filters.status'),
        placeholder: this.translate.instant('wallet.filters.all_statuses'),
        options: [
          { label: this.translate.instant('wallet.payouts.statuses.created'), value: 'created' },
          { label: this.translate.instant('wallet.payouts.statuses.completed'), value: 'completed' },
          { label: this.translate.instant('wallet.payouts.statuses.failed'), value: 'failed' },
          { label: this.translate.instant('wallet.payouts.statuses.reversed'), value: 'reversed' },
        ],
      },
      {
        kind: 'select',
        key: 'method',
        label: this.translate.instant('wallet.filters.method'),
        placeholder: this.translate.instant('wallet.filters.all_methods'),
        options: [
          { label: this.translate.instant('wallet.payouts.methods.bank_transfer'), value: 'bank_transfer' },
          { label: this.translate.instant('wallet.payouts.methods.cash'), value: 'cash' },
          { label: this.translate.instant('wallet.payouts.methods.cheque'), value: 'cheque' },
          { label: this.translate.instant('wallet.payouts.methods.other'), value: 'other' },
        ],
      },
      {
        kind: 'dateRange',
        key: 'createdAt',
        label: this.translate.instant('wallet.filters.date'),
        placeholder: this.translate.instant('wallet.filters.date_placeholder'),
      },
    ];
  }

  onTransactionsSearchChange(event: any) {
    const value = event.target.value.trim().replace(/^#/, '');
    this.transactionsSearchSubject.next(value);
  }

  onPayoutsSearchChange(event: any) {
    const value = event.target.value.trim().replace(/^#/, '');
    this.payoutsSearchSubject.next(value);
  }


  getWallet() {
    this.apiService.getWallet().subscribe({
      next: wallet => {
        this.wallet = wallet;
      },
      error: () => {
        this.toastNotificationService.createNotification(this.translate.instant('wallet.errors.wallet_load_failed'), ToastNotificationType.ERROR, this.translate.instant('wallet.errors.load_failed_title'), ToastPosition.TOP_RIGHT);
      }
    });
  }

  getTransactions() {
    this.transactionsResponse = undefined;
    this.apiService.getTransactions(this.transactionsPagination, this.transactionsSearch, this.toTransactionsApiFilters()).subscribe({
      next: res => {
        this.transactionsResponse = res;
      },
      error: () => {
        this.toastNotificationService.createNotification(this.translate.instant('wallet.errors.transactions_load_failed'), ToastNotificationType.ERROR, this.translate.instant('wallet.errors.load_failed_title'), ToastPosition.TOP_RIGHT);
      }
    });
  }

  getPayouts() {
    this.payoutsResponse = undefined;
    this.apiService.getPayouts(this.payoutsPagination, this.payoutsSearch, this.toPayoutsApiFilters()).subscribe({
      next: res => {
        this.payoutsResponse = res;
      },
      error: () => {
        this.toastNotificationService.createNotification(this.translate.instant('wallet.errors.payouts_load_failed'), ToastNotificationType.ERROR, this.translate.instant('wallet.errors.load_failed_title'), ToastPosition.TOP_RIGHT);
      }
    });
  }

  onTransactionsPageChange(pagination: LimitOffsetPagination) {
    this.transactionsPagination = pagination;
    this.getTransactions();
  }

  onPayoutsPageChange(pagination: LimitOffsetPagination) {
    this.payoutsPagination = pagination;
    this.getPayouts();
  }

  onTransactionsFiltersChange(values: FilterValues) {
    this.transactionsFilterValues = values;
    this.transactionsPagination = { limit: this.transactionsPagination.limit, offset: 0 };
    this.getTransactions();
  }

  onPayoutsFiltersChange(values: FilterValues) {
    this.payoutsFilterValues = values;
    this.payoutsPagination = { limit: this.payoutsPagination.limit, offset: 0 };
    this.getPayouts();
  }

  private toPayoutsApiFilters(): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    const amount = this.payoutsFilterValues['amount'];
    if (amount?.min != null) out['min_amount'] = amount.min;
    if (amount?.max != null) out['max_amount'] = amount.max;
    if (this.payoutsFilterValues['status'] != null) out['status'] = this.payoutsFilterValues['status'];
    if (this.payoutsFilterValues['method'] != null) out['method'] = this.payoutsFilterValues['method'];
    const range = this.payoutsFilterValues['createdAt'];
    if (range?.start) out['created_from'] = range.start;
    if (range?.end) out['created_to'] = range.end;
    return out;
  }

  private toTransactionsApiFilters(): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    const amount = this.transactionsFilterValues['amount'];
    if (amount?.min != null) out['min_amount'] = amount.min;
    if (amount?.max != null) out['max_amount'] = amount.max;
    if (this.transactionsFilterValues['kind'] != null) out['kind'] = this.transactionsFilterValues['kind'];
    const range = this.transactionsFilterValues['createdAt'];
    if (range?.start) out['created_from'] = range.start;
    if (range?.end) out['created_to'] = range.end;
    return out;
  }

  isExportingTransactions = false;
  isExportingPayouts = false;

  onExportTransactionsExcel(): void {
    if (this.isExportingTransactions) return;
    this.isExportingTransactions = true;
    this.apiService.exportTransactions(this.transactionsSearch, this.toTransactionsApiFilters()).subscribe({
      next: blob => {
        this.triggerDownload(blob, 'wallet-transactions');
        this.isExportingTransactions = false;
      },
      error: () => {
        this.toastNotificationService.createNotification(this.translate.instant('wallet.errors.transactions_export_failed'), ToastNotificationType.ERROR, this.translate.instant('wallet.errors.export_failed_title'), ToastPosition.TOP_RIGHT);
        this.isExportingTransactions = false;
      },
    });
  }

  onExportPayoutsExcel(): void {
    if (this.isExportingPayouts) return;
    this.isExportingPayouts = true;
    this.apiService.exportPayouts(this.payoutsSearch, this.toPayoutsApiFilters()).subscribe({
      next: blob => {
        this.triggerDownload(blob, 'payouts');
        this.isExportingPayouts = false;
      },
      error: () => {
        this.toastNotificationService.createNotification(this.translate.instant('wallet.errors.payouts_export_failed'), ToastNotificationType.ERROR, this.translate.instant('wallet.errors.export_failed_title'), ToastPosition.TOP_RIGHT);
        this.isExportingPayouts = false;
      },
    });
  }

  private triggerDownload(blob: Blob, baseName: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseName}-${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}
